import { mergeAzureConfig } from "./installationConfigUtils";

export const BRIGHTNESS_MODES = {
  STANDARD: "standard",
  HDR_AUTO: "hdr_auto",
  CONTRAST: "contrast",
};

const REFERENCE_COEFFICIENT = 1;

const SHADOW_STEPS = [0.7, 0.45, 0.25];

const HIGHLIGHT_STEPS = [1.5, 3, 5];

export const BRIGHTNESS_PRESETS = {
  balanced: { shadowLevel: 2, highlightLevel: 2 },
  shadowPriority: { shadowLevel: 3, highlightLevel: 0 },
  highlightPriority: { shadowLevel: 0, highlightLevel: 3 },
  wide: { shadowLevel: 3, highlightLevel: 3 },
};

const COEFF_TOLERANCE = 0.06;

function normalizeCoefficientsList(config) {
  const raw = config?.sspd_ref_coefficient_list;
  if (!Array.isArray(raw) || raw.length === 0) {
    return [REFERENCE_COEFFICIENT];
  }
  return raw
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((a, b) => a - b);
}

function isHdrEnabled(config) {
  const hdr = config?.hdr;
  return hdr === 1 || hdr === true;
}

function hasCoefficient(coefficients, target) {
  return coefficients.some(
    (value) => Math.abs(value - target) < COEFF_TOLERANCE,
  );
}

function isReferenceOnlyList(coefficients) {
  return (
    coefficients.length === 1 &&
    hasCoefficient(coefficients, REFERENCE_COEFFICIENT)
  );
}

export function buildCoefficientList(shadowLevel, highlightLevel) {
  const dark = SHADOW_STEPS.slice(0, Math.max(0, Math.min(3, shadowLevel)));
  const bright = HIGHLIGHT_STEPS.slice(
    0,
    Math.max(0, Math.min(3, highlightLevel)),
  );
  return [...new Set([...dark, REFERENCE_COEFFICIENT, ...bright])].sort(
    (a, b) => a - b,
  );
}

export function inferShadowLevel(coefficients) {
  for (let level = 3; level >= 1; level -= 1) {
    const expected = SHADOW_STEPS.slice(0, level);
    if (expected.every((step) => hasCoefficient(coefficients, step))) {
      return level;
    }
  }
  return 0;
}

export function inferHighlightLevel(coefficients) {
  for (let level = 3; level >= 1; level -= 1) {
    const expected = HIGHLIGHT_STEPS.slice(0, level);
    if (expected.every((step) => hasCoefficient(coefficients, step))) {
      return level;
    }
  }
  return 0;
}

export function parseBrightnessModeFromConfig(config) {
  const coefficients = normalizeCoefficientsList(config);

  if (!isHdrEnabled(config)) {
    return {
      mode: BRIGHTNESS_MODES.STANDARD,
      shadowLevel: 0,
      highlightLevel: 0,
      coefficients: [REFERENCE_COEFFICIENT],
    };
  }

  if (isReferenceOnlyList(coefficients)) {
    return {
      mode: BRIGHTNESS_MODES.HDR_AUTO,
      shadowLevel: 0,
      highlightLevel: 0,
      coefficients: [REFERENCE_COEFFICIENT],
    };
  }

  return {
    mode: BRIGHTNESS_MODES.CONTRAST,
    shadowLevel: inferShadowLevel(coefficients),
    highlightLevel: inferHighlightLevel(coefficients),
    coefficients,
  };
}

export function buildBrightnessAzureOutput(
  configData,
  { mode, shadowLevel, highlightLevel },
) {
  if (mode === BRIGHTNESS_MODES.STANDARD) {
    return mergeAzureConfig(configData, {
      hdr: 0,
      sspd_ref_coefficient_list: [REFERENCE_COEFFICIENT],
    });
  }

  if (mode === BRIGHTNESS_MODES.HDR_AUTO) {
    return mergeAzureConfig(configData, {
      hdr: 1,
      sspd_ref_coefficient_list: [REFERENCE_COEFFICIENT],
    });
  }

  return mergeAzureConfig(configData, {
    hdr: 1,
    sspd_ref_coefficient_list: buildCoefficientList(
      shadowLevel,
      highlightLevel,
    ),
  });
}

export function isContrastSelectionValid(shadowLevel, highlightLevel) {
  return shadowLevel > 0 || highlightLevel > 0;
}

function getShadowLevelLabel(t, level) {
  if (level === 0) {
    return t("installation_brightness_shadows_none");
  }
  if (level === 1) {
    return t("installation_brightness_shadows_light");
  }
  if (level === 2) {
    return t("installation_brightness_shadows_medium");
  }
  return t("installation_brightness_shadows_strong");
}

function getHighlightLevelLabel(t, level) {
  if (level === 0) {
    return t("installation_brightness_highlights_none");
  }
  if (level === 1) {
    return t("installation_brightness_highlights_light");
  }
  if (level === 2) {
    return t("installation_brightness_highlights_medium");
  }
  return t("installation_brightness_highlights_strong");
}

export function formatBrightnessSummary(
  t,
  { mode, shadowLevel, highlightLevel, coefficients },
) {
  if (mode === BRIGHTNESS_MODES.STANDARD) {
    return t("installation_brightness_summary_standard");
  }
  if (mode === BRIGHTNESS_MODES.HDR_AUTO) {
    return t("installation_brightness_summary_hdr_auto");
  }

  const count =
    coefficients?.length ||
    buildCoefficientList(shadowLevel, highlightLevel).length;
  return t("installation_brightness_summary_contrast", {
    count,
    shadow: getShadowLevelLabel(t, shadowLevel),
    highlight: getHighlightLevelLabel(t, highlightLevel),
  });
}

export function formatBrightnessSubtitle(t, config) {
  const state = parseBrightnessModeFromConfig(config);
  if (state.mode === BRIGHTNESS_MODES.STANDARD) {
    return t("installation_brightness_row_subtitle_standard");
  }
  if (state.mode === BRIGHTNESS_MODES.HDR_AUTO) {
    return t("installation_brightness_row_subtitle_hdr_auto");
  }
  return formatBrightnessSummary(t, state);
}

export function areBrightnessStatesEqual(stateA, stateB) {
  return (
    stateA.mode === stateB.mode &&
    stateA.shadowLevel === stateB.shadowLevel &&
    stateA.highlightLevel === stateB.highlightLevel
  );
}
